import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { authPaths } from '../../constants';
import { UserRolesResolve } from '../../user';

import { LoanResolverService } from '../services/loan-resolver.service';
import { PipelineDataResolver } from '../services/pipeline-data.resolver';

@Injectable()
export class AppSubmittedCheckGuard implements CanActivate {
  constructor(
    private loanResolverService: LoanResolverService,
    private pipelineDataResolverService: PipelineDataResolver,
    private userRolesResolve: UserRolesResolve,
    private router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const paramLoanId = +route.paramMap.get('loanId');
    const paramNmls = route.paramMap.get('nmls');
    let nmls = !authPaths.includes(paramNmls) ? paramNmls : null;
    const isApplicationURL = !state.url.includes('tasks');
    const seq: Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree = new Observable((observer) => {
      let user;
      this.userRolesResolve
        .getCurrentUserWithACL()
        .pipe(
          switchMap((userr) => {
            user = userr;
            return this.pipelineDataResolverService.getPipelines();
          })
        )
        .subscribe((data) => {
          if (user && user.Loans) {
            const currLoan = user.Loans.find((loan) => loan.LoanId === paramLoanId);
            nmls = currLoan?.LoanOfficer?.nmlsId || nmls;
            const excludedStatuses = this.pipelineDataResolverService.closedLoanStages;
            if (!currLoan || excludedStatuses.includes(currLoan.CurrentStage?.StageId)) {
              observer.next(this.router.createUrlTree(nmls ? [nmls, 'pos', 'app', 'select-loan'] : ['pos', 'app', 'select-loan']));
              observer.complete();
            } else if (currLoan.inviteType === 'DOCUMENT_ONLY') {
              observer.next(
                isApplicationURL
                  ? this.router.createUrlTree(nmls ? [nmls, 'pos', 'app', paramLoanId, 'tasks'] : ['pos', 'app', paramLoanId, 'tasks'])
                  : true
              );
              observer.complete();
            } else {
              this.loanResolverService.getLoan(paramLoanId).subscribe((loan) => {
                let urlHasNmls = false;
                const path = state.url.split('/').filter((part) => !!part);
                if (path.length > 1 && path[0] && !authPaths.includes(path[0])) {
                  urlHasNmls = true;
                }
                if (loan && loan.appSubmissionDate) {
                  observer.next(
                    isApplicationURL
                      ? this.router.createUrlTree(nmls ? [nmls, 'pos', 'app', paramLoanId, 'tasks'] : ['pos', 'app', paramLoanId, 'tasks'])
                      : urlHasNmls
                      ? true
                      : nmls
                      ? this.router.createUrlTree([nmls, ...path])
                      : true
                  );
                } else if (currLoan.inviteType === 'REVIEW_ONLY') {
                  observer.next(
                    (isApplicationURL && state.url.includes('review')) ||
                      this.router.createUrlTree(
                        nmls
                          ? [nmls, 'pos', 'app', paramLoanId, 'application', 'review']
                          : ['pos', 'app', paramLoanId, 'application', 'review']
                      )
                  );
                } else {
                  observer.next(
                    isApplicationURL
                      ? urlHasNmls
                        ? true
                        : nmls
                        ? this.router.createUrlTree([nmls, ...path])
                        : true
                      : this.router.createUrlTree(
                          nmls ? [nmls, 'pos', 'app', paramLoanId, 'application'] : ['pos', 'app', paramLoanId, 'application']
                        )
                  );
                }
                observer.complete();
              });
            }
          } else {
            observer.next(true);
            observer.complete();
          }
        });
    });
    return seq;
  }
}
